// T-373 — Data layer del Menu Manager in-app.
//
// Editing ONLINE-diretto a Supabase (RLS-gated), NON attraverso la coda di
// sync offline: modificare il menù non è un'azione HACCP offline-first e le
// tabelle menu_* non sono in MASTER_TABLES. Ogni riga letta viene Zod-parsata
// (schemi in @trackeat/shared) per validare al boundary; le scritture bumpano
// `version` (LWW, bassa contesa — nessuna UI di conflitto OCC).

import {
  MenuCategorySchema,
  MenuItemSchema,
  AllergenSchema,
  DietaryFlagSchema,
  type MenuCategory,
  type MenuItem,
  type MenuCategoryInput,
  type MenuItemInput,
  type TakeawayPrices,
} from '@trackeat/shared';
import { getSupabase } from '../../../lib/supabase.js';
import { MENU_PHOTO_MAX_BYTES } from '../../../lib/imageCompress.js';

export interface MenuBusiness {
  id: string;
  name: string;
  menu_public_slug: string | null;
  menu_published: boolean;
  menu_theme: {
    takeaway_prices?: TakeawayPrices;
    palette?: { paper?: string; ink?: string; primary?: string; accent?: string };
  } | null;
}

export interface MenuSnapshot {
  business: MenuBusiness;
  categories: MenuCategory[];
  items: MenuItem[];
}

import { menuTables, type MenuType } from './menuType.js';

const PHOTO_BUCKET = 'menu-photos';

// I menù dei clienti storici contengono valori legacy non canonici (es.
// allergene `frutta-a-guscio` invece di `frutta_guscio`, dietary `lactose_free`
// non supportato). La lettura NON deve mai far crashare l'editor: puliamo i
// valori noti e, se il parse fallisce comunque, ripieghiamo su un record minimo
// valido invece di rompere l'intero menù. La validazione STRETTA resta sulle
// scritture (input utente).
const CANON_ALLERGENS = new Set<string>(AllergenSchema.options);
const ALLERGEN_ALIASES: Record<string, string> = {
  'frutta-a-guscio': 'frutta_guscio',
  'frutta a guscio': 'frutta_guscio',
};
const CANON_DIET = new Set<string>(DietaryFlagSchema.options);

function cleanAllergens(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const out = new Set<string>();
  for (const a of v) {
    const mapped = ALLERGEN_ALIASES[String(a)] ?? String(a);
    if (CANON_ALLERGENS.has(mapped)) out.add(mapped);
  }
  return [...out];
}
function cleanDietary(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return [...new Set(v.map(String).filter((d) => CANON_DIET.has(d)))];
}

function normalizeCategory(row: Record<string, unknown>): MenuCategory {
  const candidate = {
    ...row,
    name: (row.name as string) || '(senza nome)',
    translations: row.translations ?? {},
  };
  const res = MenuCategorySchema.safeParse(candidate);
  if (res.success) return res.data;
  // Fallback tollerante: costruiamo un record valido dai campi essenziali.
  return MenuCategorySchema.parse({
    id: row.id,
    tenant_id: row.tenant_id,
    business_id: row.business_id,
    name: (row.name as string) || '(senza nome)',
    icon: (row.icon as string) ?? null,
    sort_order: typeof row.sort_order === 'number' ? row.sort_order : 0,
    visible: row.visible !== false,
    section: row.section === 'bar' ? 'bar' : 'food',
    version: typeof row.version === 'number' ? row.version : 1,
  });
}

function normalizeItem(row: Record<string, unknown>): MenuItem {
  const candidate = {
    ...row,
    name: (row.name as string) || '(senza nome)',
    allergens: cleanAllergens(row.allergens),
    dietary_flags: cleanDietary(row.dietary_flags),
    translations: row.translations ?? {},
  };
  const res = MenuItemSchema.safeParse(candidate);
  if (res.success) return res.data;
  // Fallback tollerante: mai far fallire l'intero menù per una riga anomala.
  return MenuItemSchema.parse({
    id: row.id,
    tenant_id: row.tenant_id,
    business_id: row.business_id,
    category_id: row.category_id,
    name: (row.name as string) || '(senza nome)',
    description: typeof row.description === 'string' ? row.description : '',
    price_cents:
      typeof row.price_cents === 'number' && Number.isInteger(row.price_cents) && row.price_cents >= 0
        ? row.price_cents
        : null,
    allergens: cleanAllergens(row.allergens),
    dietary_flags: cleanDietary(row.dietary_flags),
    visible: row.visible !== false,
    out_of_stock: row.out_of_stock === true,
    photo_url: (row.photo_url as string) ?? null,
    version: typeof row.version === 'number' ? row.version : 1,
  });
}

export async function fetchMenu(businessId: string, menuType: MenuType = 'menu'): Promise<MenuSnapshot> {
  const sb = getSupabase();
  const [bRes, cRes, iRes] = await Promise.all([
    sb
      .from('businesses')
      .select('id,name,menu_public_slug,menu_published,menu_theme')
      .eq('id', businessId)
      .single(),
    sb.from(menuTables(menuType).categories).select('*').eq('business_id', businessId).order('sort_order', { ascending: true }),
    sb.from(menuTables(menuType).items).select('*').eq('business_id', businessId).order('sort_order', { ascending: true }),
  ]);
  if (bRes.error) throw bRes.error;
  if (cRes.error) throw cRes.error;
  if (iRes.error) throw iRes.error;
  return {
    business: bRes.data as MenuBusiness,
    categories: (cRes.data ?? []).map((r) => normalizeCategory(r as Record<string, unknown>)),
    items: (iRes.data ?? []).map((r) => normalizeItem(r as Record<string, unknown>)),
  };
}

// ── Categorie ────────────────────────────────────────────────────────────────
export async function createCategory(
  businessId: string,
  tenantId: string,
  input: MenuCategoryInput,
  nextSortOrder: number,
  menuType: MenuType = 'menu',
): Promise<MenuCategory> {
  const sb = getSupabase();
  const { data, error } = await sb
    .from(menuTables(menuType).categories)
    .insert({
      tenant_id: tenantId,
      business_id: businessId,
      name: input.name,
      icon: input.icon ?? null,
      section: input.section ?? 'food',
      visible: input.visible ?? true,
      sort_order: input.sort_order ?? nextSortOrder,
    })
    .select('*')
    .single();
  if (error || !data) throw error ?? new Error('insert_failed');
  return normalizeCategory(data as Record<string, unknown>);
}

export async function updateCategory(
  id: string,
  patch: Partial<MenuCategory>,
  currentVersion: number,
  menuType: MenuType = 'menu',
): Promise<MenuCategory> {
  const sb = getSupabase();
  const { data, error } = await sb
    .from(menuTables(menuType).categories)
    .update({ ...patch, version: currentVersion + 1 })
    .eq('id', id)
    .select('*')
    .single();
  if (error || !data) throw error ?? new Error('update_failed');
  return normalizeCategory(data as Record<string, unknown>);
}

export async function deleteCategory(id: string, menuType: MenuType = 'menu'): Promise<void> {
  const sb = getSupabase();
  const { error } = await sb.from(menuTables(menuType).categories).delete().eq('id', id);
  if (error) throw error;
}

// ── Piatti ───────────────────────────────────────────────────────────────────
export async function createItem(
  businessId: string,
  tenantId: string,
  input: MenuItemInput,
  nextSortOrder: number,
  menuType: MenuType = 'menu',
): Promise<MenuItem> {
  const sb = getSupabase();
  const { data, error } = await sb
    .from(menuTables(menuType).items)
    .insert({
      tenant_id: tenantId,
      business_id: businessId,
      category_id: input.category_id,
      name: input.name,
      description: input.description ?? '',
      price_cents: input.price_cents ?? null,
      price_unit: input.price_unit ?? null,
      allergens: input.allergens ?? [],
      dietary_flags: input.dietary_flags ?? [],
      visible: input.visible ?? true,
      out_of_stock: input.out_of_stock ?? false,
      photo_url: input.photo_url ?? null,
      translations: input.translations ?? {},
      sort_order: input.sort_order ?? nextSortOrder,
    })
    .select('*')
    .single();
  if (error || !data) throw error ?? new Error('insert_failed');
  return normalizeItem(data as Record<string, unknown>);
}

export async function updateItem(
  id: string,
  patch: Partial<MenuItem>,
  currentVersion: number,
  menuType: MenuType = 'menu',
): Promise<MenuItem> {
  const sb = getSupabase();
  const { data, error } = await sb
    .from(menuTables(menuType).items)
    .update({ ...patch, version: currentVersion + 1 })
    .eq('id', id)
    .select('*')
    .single();
  if (error || !data) throw error ?? new Error('update_failed');
  return normalizeItem(data as Record<string, unknown>);
}

export async function deleteItem(id: string, menuType: MenuType = 'menu'): Promise<void> {
  const sb = getSupabase();
  const { error } = await sb.from(menuTables(menuType).items).delete().eq('id', id);
  if (error) throw error;
}

/** Riordino batch: assegna sort_order 10,20,30… nell'ordine fornito. */
export async function reorderItems(orderedIds: string[], menuType: MenuType = 'menu'): Promise<void> {
  const sb = getSupabase();
  await Promise.all(
    orderedIds.map(async (id, idx) => {
      const { error } = await sb.from(menuTables(menuType).items).update({ sort_order: (idx + 1) * 10 }).eq('id', id);
      if (error) throw error;
    }),
  );
}
export async function reorderCategories(orderedIds: string[], menuType: MenuType = 'menu'): Promise<void> {
  const sb = getSupabase();
  await Promise.all(
    orderedIds.map(async (id, idx) => {
      const { error } = await sb.from(menuTables(menuType).categories).update({ sort_order: (idx + 1) * 10 }).eq('id', id);
      if (error) throw error;
    }),
  );
}

export async function setPublished(businessId: string, next: boolean): Promise<void> {
  const sb = getSupabase();
  const { error } = await sb.from('businesses').update({ menu_published: next }).eq('id', businessId);
  if (error) throw error;
}

// ── Foto ─────────────────────────────────────────────────────────────────────
// Path stabile per-id: `<slug>/<itemId>.jpg` (i rinomini non orfanano la foto).
// Ritorna la public URL con cache-bust `?v=<epoch>` da salvare in photo_url.
export async function uploadItemPhoto(slug: string, itemId: string, jpegBlob: Blob): Promise<string> {
  if (jpegBlob.type !== 'image/jpeg' || jpegBlob.size > MENU_PHOTO_MAX_BYTES) {
    throw new Error('menu_photo_policy_violation');
  }
  const sb = getSupabase();
  const path = `${slug}/${itemId}.jpg`;
  const bucket = sb.storage.from(PHOTO_BUCKET);
  const options = {
    contentType: 'image/jpeg',
    cacheControl: '604800',
  };
  // `upload` crea il file; per un percorso già presente usiamo `update`,
  // perché è l'operazione Storage che sostituisce anche i metadati di cache.
  let { error } = await bucket.upload(path, jpegBlob, { ...options, upsert: false });
  if (error) {
    ({ error } = await bucket.update(path, jpegBlob, options));
  }
  if (error) throw error;
  const { data } = bucket.getPublicUrl(path);
  return `${data.publicUrl}?v=${Date.now()}`;
}

export async function removeItemPhoto(slug: string, itemId: string): Promise<void> {
  const sb = getSupabase();
  // best-effort: se l'oggetto non c'è non è un errore bloccante
  await sb.storage.from(PHOTO_BUCKET).remove([`${slug}/${itemId}.jpg`]);
}
